import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {delay, first, takeUntil, tap} from 'rxjs/operators';
import {Uuid} from '../../../shared/interface/response/uuid';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UserService} from '../../../shared/services/user.service';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {ReplaySubject} from 'rxjs';
import {isValidPesel} from '../../../shared/validator/pesel-validator';

@Component({
  selector: 'app-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styleUrls: ['./change-password-dialog.component.scss']
})
export class ChangePasswordDialogComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  passwordForm: FormGroup;
  processingRequest = false;
  enableSaveButton = false;
  formErrors: any = {};

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private userService: UserService,
    private formHelper: FormHelperService,
  ) {
    this.passwordForm = fb.group({
      currentPassword: fb.control('', [Validators.required]),
      newPassword: fb.control('',
        [Validators.required,
          Validators.minLength(12),
          this.hasTwoCapitalLetters(),
          this.hasTwoSmallLetters(),
          this.hasOneDigit(),
          this.hasOneSpecialChar(),
        ]),
      repeatPassword: fb.control('', [Validators.required, this.validRepeatedPassword()]),
    });
  }

  validRepeatedPassword(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null =>
      (!this.passwordForm || this.passwordForm.get('newPassword').value === this.passwordForm.get('repeatPassword').value)
        ? null : {invalidRepeatedPassword: true};
  }

  hasTwoCapitalLetters(): ValidatorFn {
    const regex = /^(?=(?:.*[A-Z]){2,})(.{2,})$/;

    return (control: AbstractControl): { [key: string]: any } | null =>
        regex.test(control.value) ? null : {twoCapitalLetters: true};
  }

  hasTwoSmallLetters(): ValidatorFn {
    const regex = /^(?=(?:.*[a-z]){2,})(.{2,})$/;

    return (control: AbstractControl): { [key: string]: any } | null =>
      regex.test(control.value) ? null : {twoSmallLetters: true};
  }

  hasOneDigit(): ValidatorFn {
    const regex = /^(?=(?:.*[0-9]){1,})(.{1,})$/;

    return (control: AbstractControl): { [key: string]: any } | null =>
      regex.test(control.value) ? null : {oneDigit: true};
  }

  hasOneSpecialChar(): ValidatorFn {
    const regex = /^(?=(?:.*[!@#$%^&*()\-_=+{};:,<.>]){1,})(.{8,})$/;

    return (control: AbstractControl): { [key: string]: any } | null =>
      regex.test(control.value) ? null : {oneSpecialChar: true};
  }

  ngOnInit(): void {
    this.passwordForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0),
        tap(() => this.enableSaveButton = this.passwordForm.valid)
      )
      .subscribe();

    this.passwordForm.get('newPassword').valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe(() => {
        this.passwordForm.get('repeatPassword').reset();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  sendForm(): void {
    if (!this.passwordForm.valid) {
      this.passwordForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.passwordForm, this.formErrors);
    this.formErrors = {};

    this.userService.changePassword(this.passwordForm.getRawValue())
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snack.open('Pomyślnie zaktualizowano hasło. Nastąpi wylogowanie z systemu.', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.dialogRef.close();
        setTimeout(() => {
          window.location.reload();
        }, 4000);
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        this.snack.open('Podczas aktualizacji hasła wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.passwordForm, this.formErrors);
        }
      })
    ;
  }
}
